import React from "react";
import { Stack, Button, IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useGridSelector,
  gridFilterModelSelector,
  useGridApiContext,
} from "@mui/x-data-grid-pro";

const CustomHeaderFilter = (props) => {
  const { colDef, width, height, hasFocus, colIndex, tabIndex } = props;
  const apiRef = useGridApiContext();
  const cellRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (hasFocus && cellRef.current) {
      const focusableElement = cellRef.current.querySelector('[tabindex="0"]');
      const elementToFocus = focusableElement || cellRef.current;
      elementToFocus?.focus();
    }
  }, [hasFocus]);

  const publish = React.useCallback(
    (eventName, propHandler) => (event) => {
      if (!apiRef?.current) return;

      const params = apiRef.current.getColumnHeaderParams(colDef.field);
      if (!params) return;

      apiRef.current.publishEvent(eventName, params, event);

      if (propHandler) {
        propHandler(event);
      }
    },
    [apiRef, colDef.field]
  );

  const onMouseDown = React.useCallback(
    (event) => {
      if (!hasFocus && apiRef?.current) {
        cellRef.current?.focus();
        apiRef.current.setColumnHeaderFilterFocus(colDef.field, event);
      }
    },
    [apiRef, colDef.field, hasFocus]
  );

  const mouseEventsHandlers = React.useMemo(
    () => ({
      onKeyDown: publish("headerFilterKeyDown"),
      onClick: publish("headerFilterClick"),
      onMouseDown: publish("headerFilterMouseDown", onMouseDown),
    }),
    [publish, onMouseDown]
  );

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const activeFiltersCount =
    filterModel.items?.filter(({ field }) => field === colDef.field).length ??
    0;

  const handleClearFilter = (event) => {
    event.stopPropagation();
    if (apiRef?.current) {
      const newFilterModel = {
        ...filterModel,
        items: filterModel.items.filter((item) => item.field !== colDef.field),
      };
      apiRef.current.setFilterModel(newFilterModel);
    }
  };

  return (
    <Stack
      sx={[
        {
          borderBottom: `1px solid var(--DataGrid-rowBorderColor)`,
          height: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "row",
        },
        hasFocus
          ? {
              outline: "solid #1976d2 1px",
              outlineOffset: -2,
            }
          : {
              outline: "",
              outlineOffset: 0,
            },
      ]}
      tabIndex={tabIndex}
      ref={cellRef}
      data-field={colDef.field}
      width={width}
      height={height}
      justifyContent="center"
      alignItems="center"
      role="columnheader"
      aria-colindex={colIndex + 1}
      aria-label={colDef.headerName ?? colDef.field}
      {...mouseEventsHandlers}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Button
          centerRipple={false}
          onClick={() => apiRef.current?.showFilterPanel(colDef.field)}
          size="small"
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          {activeFiltersCount > 0 ? `${activeFiltersCount} active` : "Add"}{" "}
          filters
        </Button>
        {activeFiltersCount > 0 && (
          <IconButton
            size="small"
            onClick={handleClearFilter}
            sx={{
              padding: "2px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Stack>
  );
};

export default CustomHeaderFilter;
